import { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { parseLocalizedNumber } from '@/lib/currency';

interface NumericCellEditorProps {
  value: unknown;
  ariaLabel?: string;
  /**
   * Locale for decimal/group-separator parsing (e.g. 'de-DE' → comma is the
   * decimal). Falls back to runtime default. Display-side formatting uses
   * the same `locale` prop passed to `NumericCell`, so display and parse
   * stay in sync.
   */
  locale?: string;
  onChange: (value: number | null) => void;
  onCommit: () => void;
  onCancel: () => void;
}

export function NumericCellEditor({
  value,
  ariaLabel,
  locale,
  onChange,
  onCommit,
  onCancel,
}: NumericCellEditorProps) {
  // Pre-populate with the locale-formatted value so the user sees the
  // familiar separators when entering edit mode. Falls back to `String`
  // when `value` isn't a finite number.
  const initialDisplay = (() => {
    if (value == null || value === '') return '';
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n)) return String(value);
    return new Intl.NumberFormat(locale, { useGrouping: false }).format(n);
  })();
  const [localValue, setLocalValue] = useState(initialDisplay);
  const inputRef = useRef<HTMLInputElement>(null);
  const localValueRef = useRef(localValue);
  localValueRef.current = localValue;
  const committedRef = useRef(false);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    // Place cursor at end (don't select — allows type-to-start to append naturally)
    const len = input.value.length;
    input.setSelectionRange(len, len);
  }, []);

  const commit = useCallback(() => {
    if (committedRef.current) return;
    committedRef.current = true;
    const parsed = parseLocalizedNumber(localValueRef.current, locale);
    onChange(parsed);
    onCommit();
  }, [locale, onChange, onCommit]);

  return (
    <Input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={localValue}
      aria-label={ariaLabel}
      onChange={e => setLocalValue(e.target.value)}
      onKeyDown={e => {
        if (e.key === 'Enter') { commit(); }
        if (e.key === 'Tab' && !committedRef.current) {
          committedRef.current = true;
          onChange(parseLocalizedNumber(localValueRef.current, locale));
        }
        if (e.key === 'Escape') { onCancel(); }
      }}
      onBlur={commit}
      className="cell-editing-input text-xs text-right"
    />
  );
}
